import {createStore} from 'zustand/vanilla';

export interface PerformanceObserverConfiguration {
  instrumentInputLag: boolean;
  instrumentInteractions: boolean;
  minIntervalInteraction: number;
  windowInteraction: number;
  instrumentWorkflows: boolean;
  minIntervalWorkflows: number;
  instrumentNavigation: boolean;
  minIntervalNavigation: number;
  windowNavigation: number;
}

export const DEFAULT_PERFORMANCE_OBSERVER_CONFIGURATION: Readonly<PerformanceObserverConfiguration> =
  {
    instrumentInputLag: false,
    instrumentInteractions: false,
    minIntervalInteraction: 30,
    windowInteraction: 10,
    instrumentWorkflows: false,
    minIntervalWorkflows: 0,
    instrumentNavigation: false,
    minIntervalNavigation: 0,
    windowNavigation: 6,
  };

const booleanOrDefault = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const intervalOrDefault = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : fallback;

const windowOrDefault = (value: unknown, fallback: number): number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value > 0 &&
  value <= 60
    ? value
    : fallback;

/** Statsig supplies a fixed schema; missing or invalid fields use defaults. */
export function parsePerformanceObserverConfiguration(
  value: unknown
): PerformanceObserverConfiguration {
  const config = (value ?? {}) as Partial<PerformanceObserverConfiguration>;
  const defaults = DEFAULT_PERFORMANCE_OBSERVER_CONFIGURATION;
  return {
    instrumentInputLag: booleanOrDefault(
      config.instrumentInputLag,
      defaults.instrumentInputLag
    ),
    instrumentInteractions: booleanOrDefault(
      config.instrumentInteractions,
      defaults.instrumentInteractions
    ),
    minIntervalInteraction: intervalOrDefault(
      config.minIntervalInteraction,
      defaults.minIntervalInteraction
    ),
    windowInteraction: windowOrDefault(
      config.windowInteraction,
      defaults.windowInteraction
    ),
    instrumentWorkflows: booleanOrDefault(
      config.instrumentWorkflows,
      defaults.instrumentWorkflows
    ),
    minIntervalWorkflows: intervalOrDefault(
      config.minIntervalWorkflows,
      defaults.minIntervalWorkflows
    ),
    instrumentNavigation: booleanOrDefault(
      config.instrumentNavigation,
      defaults.instrumentNavigation
    ),
    minIntervalNavigation: intervalOrDefault(
      config.minIntervalNavigation,
      defaults.minIntervalNavigation
    ),
    windowNavigation: windowOrDefault(
      config.windowNavigation,
      defaults.windowNavigation
    ),
  };
}

export const performanceObserverConfiguration =
  createStore<PerformanceObserverConfiguration>()(() => ({
    ...DEFAULT_PERFORMANCE_OBSERVER_CONFIGURATION,
  }));
